import { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

import { Card } from '@/components/ui/card'

export interface StatCardItem {
  title: string
  value: number | string
  /** Shown in the `tag` variant only; Shopify's metric cards carry no icon. */
  icon?: LucideIcon
  className?: string
  active?: boolean
  onClick?: () => void
  trend?: {
    value: number
    label: string
    isPositive?: boolean
  }
}

export type StatsCardsVariant = 'card' | 'tag'

interface StatsCardsProps {
  stats: StatCardItem[]
  variant?: StatsCardsVariant
}

/**
 * `card`: Shopify metric cards (dotted-underlined title, value, optional trend) in a row that fills the width.
 * `tag`: Shopify pill tabs with a count after each label (status filters above a list).
 */
export const StatsCards = ({ stats, variant = 'card' }: StatsCardsProps) => {
  if (variant === 'tag') {
    return (
      <div className="no-scrollbar flex gap-1 overflow-x-auto" role="tablist">
        {stats.map((stat, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            aria-selected={!!stat.active}
            onClick={stat.onClick}
            disabled={!stat.onClick}
            className={cn(
              'flex h-7 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
              stat.active ? 'bg-selected text-ink' : 'text-text-2 hover:bg-subdued hover:text-ink',
              stat.className
            )}
          >
            {stat.icon && <stat.icon className="size-4" />}
            {stat.title}
            <span className={cn('text-xs tabular-nums', stat.active ? 'text-ink' : 'text-muted-foreground')}>{stat.value}</span>
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
      {stats.map((stat, index) => (
        <Card key={index} className={cn('space-y-1 p-4', stat.className)}>
          <p className="w-fit text-sm font-semibold text-ink underline decoration-faint decoration-dotted underline-offset-4">{stat.title}</p>
          <p className="truncate text-lg font-semibold tabular-nums text-ink">{stat.value}</p>
          {stat.trend && (
            <p className="text-xs text-muted-foreground">
              <span className={cn('mr-1 font-medium', stat.trend.isPositive ? 'text-success' : 'text-error')}>
                {stat.trend.isPositive ? '+' : '-'}
                {stat.trend.value}
                {stat.trend.label}
              </span>
              since last period
            </p>
          )}
        </Card>
      ))}
    </div>
  )
}
